import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ApplicationDetail {
  application_id: number
  applicant_name: string | null
  applicant_phone: string | null
  scheduled_date: string
  start_time: string | null
  end_time: string | null
  memo: string | null
  status: number
  applied_at: string
}

export default function CounselApplicationDetail() {
  const { application_id } = useParams<{ application_id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const guestName = location.state?.guest_name
  const guestPhone = location.state?.guest_phone

  const [application, setApplication] = useState<ApplicationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [cancelModal, setCancelModal] = useState(false)

  useEffect(() => {
    fetchDetail()
  }, [application_id])

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')

      if (token) {
        // 회원
        const res = await fetch(getApiUrl(`/marubit/api/counseling/applications/me/${application_id}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error('조회 실패')
        setApplication(await res.json())
      } else if (guestName && guestPhone) {
        // 비회원
        const res = await fetch(getApiUrl(`/marubit/api/counseling/guest/applications/${application_id}/detail`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guest_name: guestName, guest_phone: guestPhone }),
        })
        if (!res.ok) throw new Error('조회 실패')
        setApplication(await res.json())
      } else {
        navigate('/counsel/check')
      }
    } catch {
      alert('신청 정보를 불러오는 데 실패했습니다.')
      navigate('/counsel/check')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    try {
      const token = localStorage.getItem('access_token')

      if (token) {
        const res = await fetch(getApiUrl(`/marubit/api/counseling/applications/${application_id}/cancel`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          const err = await res.json()
          alert(err.detail ?? '취소 중 오류가 발생했습니다.')
          return
        }
      } else {
        const res = await fetch(getApiUrl(`/marubit/api/counseling/guest/applications/${application_id}/cancel`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guest_name: guestName, guest_phone: guestPhone }),
        })
        if (!res.ok) {
          const err = await res.json()
          alert(err.detail ?? '취소 중 오류가 발생했습니다.')
          return
        }
      }

      alert('상담 신청이 취소되었습니다.')
      navigate('/counsel/check')
    } catch {
      alert('취소 중 오류가 발생했습니다.')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const formatTime = (t: string | null) => t ? t.slice(0, 5) : '-'

  const statusLabel = (status: number) => {
    if (status === 0) return '확인대기'
    if (status === 1) return '확인완료'
    if (status === 2) return '신청취소'
    return '-'
  }

  const statusColor = (status: number) => {
    if (status === 2) return 'text-red-400 bg-red-50 border-red-100'
    if (status === 1) return 'text-green-600 bg-green-50 border-green-100'
    return 'text-[#887267] bg-[#f5f2f0] border-[#e8e0dc]'
  }

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start py-[14px] border-b border-gray-100 last:border-none">
      <span className="w-[100px] text-[13px] text-gray-400 font-medium shrink-0 pt-[1px]">{label}</span>
      <span className="text-[14px] text-[#222] font-medium">{value || '-'}</span>
    </div>
  )

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  if (!application) return null

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/counsel/check')}
          className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1"
        >
          ← 신청 목록으로
        </button>

        {/* 타이틀 */}
        <div className="mb-6">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">상담 신청 상세</h1>
        </div>

        {/* 신청 상태 */}
        <div className={`flex items-center justify-between px-6 py-4 rounded-2xl border mb-4 ${statusColor(application.status)}`}>
          <span className="text-[15px] font-bold">신청 상태</span>
          <span className="text-[15px] font-extrabold">{statusLabel(application.status)}</span>
        </div>

        {/* 상담 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            상담 정보
          </h2>
          <InfoRow label="상담 날짜" value={formatDate(application.scheduled_date)} />
          <InfoRow
            label="상담 시간"
            value={
              application.start_time && application.end_time
                ? `${formatTime(application.start_time)} ~ ${formatTime(application.end_time)}`
                : '-'
            }
          />
          {application.memo && (
            <InfoRow label="요청사항" value={application.memo} />
          )}
        </div>

        {/* 신청 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            신청 정보
          </h2>
          <InfoRow label="신청자" value={application.applicant_name ?? '-'} />
          <InfoRow label="연락처" value={application.applicant_phone ?? '-'} />
          <InfoRow label="신청일" value={formatDateTime(application.applied_at)} />
          <InfoRow label="신청번호" value={String(application.application_id)} />
        </div>

        {/* 취소 버튼 */}
        {application.status !== 2 && (
          <button
            onClick={() => setCancelModal(true)}
            className="w-full h-[52px] bg-white text-red-400 border-[1.5px] border-red-200 rounded-xl text-[16px] font-bold cursor-pointer hover:bg-red-50 transition-colors"
          >
            신청 취소
          </button>
        )}

        {/* 취소 확인 모달 */}
        {cancelModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
            <div className="bg-white rounded-2xl px-6 py-7 w-[300px] text-center shadow-[0_8px_32px_rgba(0,0,0,0.15)]">
              <p className="text-[16px] font-bold text-[#222] mb-2">신청 취소</p>
              <p className="text-[14px] text-gray-400 mb-6 leading-relaxed">상담 신청을 취소하시겠습니까?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCancelModal(false)}
                  className="flex-1 h-[42px] bg-gray-100 text-gray-500 border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-gray-200 transition-colors"
                >
                  돌아가기
                </button>
                <button
                  onClick={() => { setCancelModal(false); handleCancel() }}
                  className="flex-1 h-[42px] bg-red-400 text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-red-500 transition-colors"
                >
                  취소하기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}